import React from "react";
import { graphql, HeadProps, PageProps } from "gatsby";
import { OfferPerPromoQuery } from "types/graphql-types";
import { defaultLocalizedPromo, offerPerPromoToLocalizedOffer } from "./utils";
import { useRemotePromo } from "src/ssc-api/useRemotePromos";
import { useGeolocation } from "../../utils/localization/geolocation/GeolocationContext";
import { PageContext } from "types/pageContext";
import GatsbyHeadOpenGraph from "components/SEO/GatsbyHeadOpenGraph";
import { isPresent } from "src/utils/checks";
import { match, P } from "ts-pattern";
import OfferPerPromoNotActive from "./OfferPerPromoNotActive";
import NewHeroOfferPerPromoLoaded from "./OfferPerPromoLoaded";

type NonNullableOfferPerPromoQuery = OfferPerPromoQuery & {
  offer: NonNullable<OfferPerPromoQuery["offer"]>;
};

const OfferPerPromo: React.FC<
  React.PropsWithChildren<PageProps<NonNullableOfferPerPromoQuery>>
> = ({ data }) => {
  const showDraftContent = isPresent(process.env.SANITY_API_TOKEN);
  const offer = data.offer;
  const configurationCode = offer.configuration?.code;
  const geolocation = useGeolocation();
  const offerResponse = useRemotePromo(configurationCode);

  const status = match(offerResponse)
    .with({ status: "error" }, () => "not-active" as const)
    .with({ status: "success", data: P.nullish }, () => "not-active" as const)
    .with(
      { status: "idle" },
      { status: "loading" },
      { status: "pending" },
      () => "loading" as const
    )
    .with({ status: "success", data: P.any }, () => "active" as const)
    .exhaustive();

  const showContentForOffer =
    status == "active" || status == "loading" || showDraftContent;

  const remoteOffer = offerPerPromoToLocalizedOffer(
    offer,
    geolocation
  )(offerResponse.data);

  return (
    <React.Fragment>
      {showContentForOffer ? (
        <NewHeroOfferPerPromoLoaded
          remoteOffer={
            showContentForOffer ? remoteOffer : defaultLocalizedPromo
          }
          {...data}
        />
      ) : (
        <OfferPerPromoNotActive
          offer={data.offer}
          raqPagePath={data.raqPagePath}
        />
      )}
    </React.Fragment>
  );
};

export const Head = (
  props: HeadProps<NonNullableOfferPerPromoQuery, PageContext>
) => {
  const { data, pageContext } = props;

  return (
    <GatsbyHeadOpenGraph
      path={props.location.pathname}
      alternateLinks={pageContext.alternateLinks}
      languageCode={pageContext.languageCode}
      openGraph={data.offer.metadata?.openGraph}
      altImage={data.offer.heroImage?.asset?.url}
      excludeFromSitemap={data.offer.metadata?.excludeFromSitemap}
    />
  );
};

export const query = graphql`
  query OfferPerPromo($id: String!) {
    firstLastDepartureCruiseDates: firstLastDepartureCruiseDates {
      minYearMonth
      maxYearMonth
    }
    cruisePromosContent {
      ...FYCPromosContent
    }
    shipsContent {
      ...FYCShipsContent
    }
    themedCruisesContent {
      ...FYCThemedCruisesContent
    }
    cruisesTagContent {
      ...FYCCruisesTagContent
    }
    raqPagePath

    offer: sanityExclusiveOfferPerPromo(id: { eq: $id }) {
      configuration {
        code
        algoliaCode
        activeInEcommerce
      }

      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }

        tracking {
          name
        }
      }

      heroImage {
        asset {
          url
        }
        ...HeroResponsiveImageWithAlt
      }

      title {
        localized
      }

      ctaButtons {
        ...ButtonCTA
      }

      extendedTitle {
        localized
      }

      description {
        localized
      }

      termsAndConditionsFootnote {
        ...FilterableMultilingualBlockContent
      }

      featureTitle {
        localized
      }

      features {
        ...ExclusiveOfferFeature
      }

      customCTALabel {
        localized
      }

      secondaryCTA {
        ...PageCTA
      }

      tertiaryCTA {
        ...PageCTA
      }

      iconVertical {
        asset {
          url
        }
      }

      showCountdown

      countdownLabel {
        localized
      }

      sections {
        ...PageBuilderSections
        ... on SanitySharedSectionRef {
          ...PageBuilderSharedSectionRef
        }
      }

      sectionsBottom {
        ...PageBuilderSections
        ... on SanitySharedSectionRef {
          ...PageBuilderSharedSectionRef
        }
      }

      stickyEnabled
      stickyTitle {
        localized
      }
      stickyContent {
        localized
      }
      stickyCTA {
        ...ButtonCTA
      }
    }
  }
`;

export default React.memo(OfferPerPromo);
